import { useState } from "react"
import { Bar } from "react-chartjs-2"
import styled from "styled-components"

import { useGetReferralAgentMonthlyPlacementsQuery } from "store/api/referralsApi"

import { Spinner } from "components/common/Spinner"

import { placementsBarOptions } from "./chartConfig"
import { buildMonthlyPlacements } from "./monthlyPlacementsData"
import { Card, CardHeader, CardTitle } from "./shared"

const PLACED_COLOR = "#2db2c8"
const IN_PROGRESS_COLOR = "rgba(45, 178, 200, 0.2)"

const RangeSelect = styled.select`
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid var(--border-default);
  background: var(--surface-1);
  cursor: pointer;
  font-size: 12px;
  color: var(--text-primary);
`

const ChartArea = styled.div`
  height: 200px;
`

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: center;
  margin-top: 12px;
  font-size: 12px;
  color: var(--text-muted);
`

const Message = styled.p`
  margin: 0;
  font-size: 14px;
  color: var(--text-muted);
`

const LegendItem = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &::before {
    content: "";
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: ${({ $color }) => $color};
  }
`

export const MonthlyPlacementsCard = () => {
  const [period, setPeriod] = useState(6)
  const { data, isFetching, isError } = useGetReferralAgentMonthlyPlacementsQuery(period)
  const placements = data?.data ? buildMonthlyPlacements(data.data) : null

  const renderBody = () => {
    if (isFetching) return <Spinner />
    if (isError || !placements) return <Message>Monthly placements are unavailable right now.</Message>

    return (
      <>
        <ChartArea>
          <Bar
            data={{
              labels: placements.labels,
              datasets: [
                {
                  label: "Placed",
                  data: placements.values,
                  backgroundColor: placements.inProgress.map(isInProgress =>
                    isInProgress ? IN_PROGRESS_COLOR : PLACED_COLOR,
                  ),
                  borderRadius: 4,
                  maxBarThickness: 48,
                },
              ],
            }}
            options={placementsBarOptions}
          />
        </ChartArea>
        <Legend>
          <LegendItem $color={PLACED_COLOR}>Placed</LegendItem>
          {placements.inProgressLabel && (
            <LegendItem $color={IN_PROGRESS_COLOR}>{placements.inProgressLabel} — month in progress</LegendItem>
          )}
        </Legend>
      </>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Monthly placements</CardTitle>
        <RangeSelect
          aria-label="Monthly placements period"
          value={period}
          onChange={event => setPeriod(Number(event.target.value))}
        >
          <option value={6}>Last 6 months</option>
          <option value={12}>Last 12 months</option>
        </RangeSelect>
      </CardHeader>
      {renderBody()}
    </Card>
  )
}
